import * as mqtt from 'async-mqtt';
import { route, getMqttUrl } from '@ht/lib-plural-vue';

const { getRobotToken } = route;

export default class MQTTService {
  constructor(robotId, organisationId) {
    this.robotId = robotId;
    this.basePrefix = `/ht/${organisationId}`;
    this.scopePrefix = {
      public: 'public',
      robot: `robot/${robotId}`,
    };
    this.client = null;
    this.subscriptions = {};
  }

  async connect() {
    this.client = await mqtt.connectAsync(getMqttUrl(), {
      username: this.robotId,
      password: getRobotToken(),
      reconnectPeriod: 500,
      keepalive: 30,
    }, true);

    this.client.on('message', (topic, message) => this.processMessage(topic, message));
    this.client.on('error', (err) => {
      // eslint-disable-next-line
      console.error(err);
      this.client.end();
    });
    this.client.on('connect', () => {
      // eslint-disable-next-line
      console.log('MQTT connection established');
    });
  }

  processMessage(topic, message) {
    if (this.hasSubscriptions(topic)) {
      const handlers = this.subscriptions[topic];
      handlers.forEach((handler) => handler(null, { topic, message }));
    }
  }

  isConnected() {
    return this.client && this.client.connected;
  }

  createTopicString(scope, suffix) {
    if (scope === 'public') {
      return suffix.startsWith('public')
        ? `${this.basePrefix}/${suffix}`
        : `${this.basePrefix}/${this.scopePrefix[scope]}/${suffix}`;
    }
    return `${this.basePrefix}/${this.scopePrefix[scope]}/${suffix}`;
  }

  async publish({ scope = 'robot', suffix }, message) {
    const topic = this.createTopicString(scope, suffix);
    if (this.isConnected()) {
      await this.client.publish(topic, message);
    }
  }

  async subscribe({ scope = 'robot', suffix }, callback) {
    const topic = this.createTopicString(scope, suffix);
    try {
      if (this.isConnected()) {
        await this.client.subscribe(topic);
        if (!this.subscriptions[topic]) {
          this.subscriptions[topic] = [callback];
        } else {
          this.subscriptions[topic].push(callback);
        }
      }
    } catch (e) {
      // dismiss
    }
  }

  hasSubscriptions(topic) {
    return !!this.subscriptions[topic];
  }

  async unsubscribe({ scope = 'robot', suffix }, callback = null) {
    const topic = this.createTopicString(scope, suffix);
    let shouldUnsubscribe = false;
    if (!callback) {
      shouldUnsubscribe = true;
    } else if (this.hasSubscriptions(topic)) {
      this.subscriptions[topic] = this.subscriptions[topic].filter((h) => h.name !== callback.name);
      if (!this.subscriptions[topic].length) {
        shouldUnsubscribe = true;
      }
    }

    if (shouldUnsubscribe && this.isConnected()) {
      try {
        delete this.subscriptions[topic];
        await this.client.unsubscribe(topic);
      } catch (e) {
        // dismiss
      }
    }
  }

  async closeClient() {
    if (this.isConnected()) {
      const unsubscribePromises = Object.keys(this.subscriptions).map((topic) => this.client.unsubscribe(topic));
      await Promise.all(unsubscribePromises);
      this.subscriptions = {};
      await this.client.end();
    }
  }
}
